import type { UseFetchOptions } from "#app";

// Helper to generate a unique ID
const generateRequestId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// Get auth token - works on both client and server
const getAuthToken = async (): Promise<string> => {
  if (import.meta.server) {
    return useCookie<string>("authToken", {}).value || "";
  }

  return "";
};

/**
 * Generic wrapper around Nuxt's useFetch with automatic authentication handling
 * Uses useAsyncData + $fetch to properly handle async token fetching
 */
export const useFetchApi = <T = any>(
  url: string,
  rootOptions?: UseFetchOptions<T>,
  isAuthRequire: boolean = true
) => {
  // Use useAsyncData with $fetch to have full control over the async flow.
  // IMPORTANT: Do NOT pass UseFetchOptions directly into $fetch or useAsyncData;
  // it contains fields/Refs that are not valid for Nitro/ofetch.
  const method = String((rootOptions as any)?.method || "GET").toUpperCase();
  const querySig = JSON.stringify((rootOptions as any)?.params || (rootOptions as any)?.query || {});
  // NOTE: useAsyncData caches by key; for non-GET requests we must avoid reusing a stable key,
  // otherwise subsequent POST/PUT calls can get deduped and never reach the backend.
  const nonce = method === "GET" ? "" : `-${generateRequestId()}`;

  const asyncDataKey = (rootOptions as any)?.key || `api-${method}-${url}-${querySig}${nonce}`;

  return useAsyncData<T>(
    asyncDataKey,
    async (): Promise<T> => {
      // Get token FIRST (this properly awaits)
      let authToken = "";
      if (isAuthRequire && import.meta.server) {
        authToken = await getAuthToken();
        
        // Empty token but isAuthRequire=true → token expired, sync state
        if (!authToken) {
          console.warn("[useFetchApi] Token expected but empty — falling back to unauthenticated");
          const isAuthCookie = useCookie("isAuthenticated");
          if (isAuthCookie.value) {
            isAuthCookie.value = null; // Sync cookie state
          }
          // Continue without auth header (graceful fallback)
        }
      }
      
      // Build headers
      const headers: Record<string, string> = {
        ...rootOptions?.headers as Record<string, string>,
        "X-Request-Id": generateRequestId(),
      };
      
      if (authToken) {
        // Important: single space after Bearer
        headers.Authorization = `Bearer ${authToken}`;
      }

      if (isAuthRequire && import.meta.client) {
        return await fetchViaAuthProxy<T>(url, {
          method: (rootOptions as any)?.method,
          body: (rootOptions as any)?.body,
          query: (rootOptions as any)?.query ?? (rootOptions as any)?.params,
          headers,
        });
      }

      if (process.env.NODE_ENV !== "production") {
        // Do not log token value
        console.debug("[useFetchApi] preparing request", {
          url,
          method: (rootOptions as any)?.method || "GET",
          isAuthRequire,
          hasAuthToken: Boolean(authToken),
          hasAuthorizationHeader: Boolean(headers.Authorization),
        });
      }
      
      // Now make the actual request with $fetch
      const fetchOptions: any = {
        method: (rootOptions as any)?.method,
        body: (rootOptions as any)?.body,
        query: (rootOptions as any)?.query ?? (rootOptions as any)?.params,
        signal: (rootOptions as any)?.signal,
        headers,
        onRequest({ options }: any) {
          if (process.env.NODE_ENV !== "production") {
            const headerKeys = Array.isArray(options.headers)
              ? options.headers.map(([k]: any) => k)
              : options.headers instanceof Headers
                ? Array.from(options.headers.keys())
                : Object.keys((options.headers || {}) as any);

            console.debug("[useFetchApi] onRequest", {
              url,
              method: (rootOptions as any)?.method || "GET",
              headerKeys,
              hasAuthorizationHeader:
                options.headers instanceof Headers
                  ? options.headers.has("Authorization")
                  : Boolean((options.headers as any)?.Authorization),
            });
          }
        },
      };

      try {
        return (await $fetch(url, fetchOptions)) as T;
      } catch (error: any) {
        // Handle 401 errors
        if (error?.response?.status === 401 && isAuthRequire) {
          const ctx = useCookie<string>("isAuthenticated", {}).value;
          const refreshed = await getNewToken(`on-resp-${ctx}`);

          if (refreshed && import.meta.client) {
            try {
              return await $fetch<T>("/api/auth/proxy", {
                method: "POST",
                body: {
                  url,
                  method: (rootOptions as any)?.method,
                  body: (rootOptions as any)?.body,
                  query: (rootOptions as any)?.query ?? (rootOptions as any)?.params,
                  headers: {
                    ...headers,
                    "X-Request-Id": generateRequestId(),
                  },
                },
              });
            } catch (retryError: any) {
              if (retryError?.response?.status === 401) {
                await useUnAuthorize("retry-401");
              }
              throw retryError;
            }
          }

          // Retry once after refresh (authToken cookie may have been recreated)
          if (refreshed) {
            const retriedToken = await getAuthToken();
            if (retriedToken) {
              const retryHeaders = {
                ...headers,
                Authorization: `Bearer ${retriedToken}`,
                "X-Request-Id": generateRequestId(),
              };
              try {
                return (await $fetch(url, { ...(fetchOptions as any), headers: retryHeaders })) as T;
              } catch (retryError: any) {
                if (retryError?.response?.status === 401) {
                  await useUnAuthorize("retry-401");
                }
                throw retryError;
              }
            }
          }
        }
        if (error?.response?._data?.errors) {
          useShowValidationToasts(error.response._data.errors);
        }
        throw error;
      }
    },
    {
      // Pass through ONLY valid AsyncData options (avoid useFetch-only fields)
      server: (rootOptions as any)?.server,
      lazy: (rootOptions as any)?.lazy,
      immediate: (rootOptions as any)?.immediate,
      deep: (rootOptions as any)?.deep,
      dedupe: (rootOptions as any)?.dedupe,
      watch: (rootOptions as any)?.watch,
      default: (rootOptions as any)?.default,
      transform: (rootOptions as any)?.transform,
      pick: (rootOptions as any)?.pick,
    }
  );
};

async function fetchViaAuthProxy<T>(
  url: string,
  options: {
    method?: string;
    headers?: Record<string, string>;
    query?: Record<string, any>;
    body?: any;
  },
): Promise<T> {
  try {
    return await $fetch<T>("/api/auth/proxy", {
      method: "POST",
      body: {
        url,
        ...options,
      },
    });
  } catch (error: any) {
    if (error?.response?.status === 401) {
      await getNewToken("proxy-401");
      try {
        return await $fetch<T>("/api/auth/proxy", {
          method: "POST",
          body: {
            url,
            ...options,
            headers: {
              ...options.headers,
              "X-Request-Id": generateRequestId(),
            },
          },
        });
      } catch (retryError: any) {
        if (retryError?.response?.status === 401) {
          await useUnAuthorize("retry-401");
        }
        throw retryError;
      }
    }

    throw error;
  }
}
